import { TZDate } from "react-day-picker";
import { DatePickerRange } from "./DateRangePicker";
import { toTZDate, fromTZDate } from "./tzDateUtils";

export type CalendarSide = "left" | "right";

/**
 * Determine the next date range selection based on where the user
 * clicked in a single or dual-calendar picker.
 */
export function getNextDateRangeSelection({
  selectedDay,
  dateRange,
  timeZone,
  calendarSide,
}: {
  selectedDay: TZDate; // the day which was just clicked on, it must be a TZDate
  dateRange: DatePickerRange | undefined; // existing date range already selected
  timeZone: string; // timezone of the calendar
  calendarSide?: CalendarSide; // for dual-calendar pickers, which calendar was clicked on
}): DatePickerRange | undefined {
  const existingDateRange =
    dateRange ?
      {
        from: toTZDate(dateRange.from, timeZone),
        to: toTZDate(dateRange.to, timeZone),
      }
    : undefined;

  if (!existingDateRange) {
    return {
      from: fromTZDate(selectedDay),
      to: fromTZDate(selectedDay),
    };
  }

  const clickedTime = selectedDay.getTime();
  const fromTime = existingDateRange.from.getTime();
  const toTime = existingDateRange.to.getTime();

  // Clicking the only selected day clears the range.
  if (clickedTime === fromTime && clickedTime === toTime) {
    return undefined;
  }

  // Clicking the start collapses the range to the end.
  if (clickedTime === fromTime) {
    return {
      from: fromTZDate(existingDateRange.to),
      to: fromTZDate(existingDateRange.to),
    };
  }

  // Clicking the end collapses the range to the start.
  if (clickedTime === toTime) {
    return {
      from: fromTZDate(existingDateRange.from),
      to: fromTZDate(existingDateRange.from),
    };
  }

  // Clicking after the range extends the end.
  if (clickedTime > toTime) {
    return {
      from: fromTZDate(existingDateRange.from),
      to: fromTZDate(selectedDay),
    };
  }

  // Clicking before the range extends the start.
  if (clickedTime < fromTime) {
    return {
      from: fromTZDate(selectedDay),
      to: fromTZDate(existingDateRange.to),
    };
  }

  // If the user intent is ambiguous (as the click is somewhere
  // in-between the currently selected date range), then we prioritise
  // based on which calendar the user clicked in.
  if (calendarSide === "left") {
    return {
      from: fromTZDate(selectedDay),
      to: fromTZDate(existingDateRange.to),
    };
  }

  if (calendarSide === "right") {
    return {
      from: fromTZDate(existingDateRange.from),
      to: fromTZDate(selectedDay),
    };
  }

  // In a single-calendar picker, move the nearest range edge.
  const distanceToFrom = clickedTime - fromTime;
  const distanceToTo = toTime - clickedTime;

  if (distanceToFrom <= distanceToTo) {
    return {
      from: fromTZDate(selectedDay),
      to: fromTZDate(existingDateRange.to),
    };
  }

  return {
    from: fromTZDate(existingDateRange.from),
    to: fromTZDate(selectedDay),
  };
}
